import type { Command, HistoryEntry, HistoryState } from '@/types';

export interface HistoryResult<S> {
  readonly state: S;
  readonly history: HistoryState<S>;
}

export const createHistory = <S>(limit = 200): HistoryState<S> => ({ past: [], future: [], limit });

export const canUndo = <S>(h: HistoryState<S>): boolean => h.past.length > 0;
export const canRedo = <S>(h: HistoryState<S>): boolean => h.future.length > 0;

/**
 * Executes a command, records it, and clears the redo stack. If the previous entry
 * was executed within `mergeWindowMs` and accepts the merge, the two are coalesced
 * into one history entry (used for drags and typing).
 */
export const execute = <S>(
  state: S,
  history: HistoryState<S>,
  command: Command<S>,
  now = Date.now(),
  mergeWindowMs = 400,
): HistoryResult<S> => {
  const nextState = command.execute(state);
  // A command that changed nothing must not become an undo step or clear the redo stack.
  if (nextState === state) return { state, history };
  const last = history.past[history.past.length - 1];
  if (last && now - last.executedAt <= mergeWindowMs && last.command.merge) {
    const merged = last.command.merge(command);
    if (merged) {
      const past = [...history.past.slice(0, -1), { command: merged, executedAt: now }];
      return { state: nextState, history: { ...history, past, future: [] } };
    }
  }
  const entry: HistoryEntry<S> = { command, executedAt: now };
  const past = [...history.past, entry].slice(-history.limit);
  return { state: nextState, history: { ...history, past, future: [] } };
};

export const undo = <S>(state: S, history: HistoryState<S>): HistoryResult<S> => {
  const entry = history.past[history.past.length - 1];
  if (!entry) return { state, history };
  return {
    state: entry.command.undo(state),
    history: { ...history, past: history.past.slice(0, -1), future: [entry, ...history.future] },
  };
};

export const redo = <S>(state: S, history: HistoryState<S>): HistoryResult<S> => {
  const entry = history.future[0];
  if (!entry) return { state, history };
  return {
    state: entry.command.execute(state),
    history: { ...history, past: [...history.past, entry], future: history.future.slice(1) },
  };
};

/** Replays a list of commands from an initial state. Used for tests and the future replay feature. */
export const replay = <S>(initial: S, commands: readonly Command<S>[]): S =>
  commands.reduce((s, c) => c.execute(s), initial);
